import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';

const formatCurrency = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '₹0';
const formatPercent = (v) => v ? `${Number(v).toFixed(1)}%` : '0%';

function BanquetCrmDashboard({ token, selectedProperty, selectedOutletId = '', onNavigate }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [kpis, setKpis] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [sourcePerfomance, setSourcePerformance] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [overdueFollowups, setOverdueFollowups] = useState([]);
  const [lostAnalysis, setLostAnalysis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Date range
  const [dateRange, setDateRange] = useState('30');

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadKpis = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/dashboard/kpis?period=${dateRange}`, { headers });
      const { data } = await parseJson(res);
      if (res.ok) setKpis(data);
    } catch {}
  };

  const loadFunnel = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/dashboard/funnel?period=${dateRange}`, { headers });
      const { data } = await parseJson(res);
      if (res.ok) setFunnel(data.funnel || []);
    } catch {}
  };

  const loadSourcePerformance = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/dashboard/source-performance?period=${dateRange}`, { headers });
      const { data } = await parseJson(res);
      if (res.ok) setSourcePerformance(data.sources || []);
    } catch {}
  };

  const loadLeaderboard = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/dashboard/rep-leaderboard?period=${dateRange}`, { headers });
      const { data } = await parseJson(res);
      if (res.ok) setLeaderboard(data.leaderboard || []);
    } catch {}
  };

  const loadOverdueFollowups = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/followups/overdue`, { headers });
      const { data } = await parseJson(res);
      if (res.ok) setOverdueFollowups(data.overdue_followups || []);
    } catch {}
  };

  const loadLostAnalysis = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/dashboard/lost-analysis?period=${dateRange}`, { headers });
      const { data } = await parseJson(res);
      if (res.ok) setLostAnalysis(data.lost_reasons || []);
    } catch {}
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        loadKpis(),
        loadFunnel(),
        loadSourcePerformance(),
        loadLeaderboard(),
        loadOverdueFollowups(),
        loadLostAnalysis()
      ]);
    } finally {
      setLoading(false);
    }
  }, [headers, dateRange]);

  useEffect(() => {
    if (chainId && propertyId) loadAll();
  }, [chainId, propertyId, dateRange]);

  const maxFunnelValue = Math.max(...funnel.map(f => f.lead_count || 0), 1);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">CRM Dashboard</h2>
        <div className="flex items-center gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border rounded"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button onClick={loadAll} className="px-3 py-1 text-sm border rounded hover:bg-gray-100">
            ↻ Refresh
          </button>
          <button
            title="CRM Configuration"
            onClick={() => onNavigate && onNavigate('banquet-crm-config')}
            className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
          >
            ⚙ Config
          </button>
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">New Leads</div>
              <div className="text-2xl font-bold">{kpis?.new_leads || 0}</div>
              <div className="text-xs text-gray-400 mt-1">Period total</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Pipeline</div>
              <div className="text-2xl font-bold text-blue-600">{kpis?.leads_in_pipeline || 0}</div>
              <div className="text-xs text-gray-400 mt-1">Active leads</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Conversion Rate</div>
              <div className="text-2xl font-bold text-green-600">{formatPercent(kpis?.conversion_rate)}</div>
              <div className="text-xs text-gray-400 mt-1">Won / Terminal</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pipeline Value</div>
              <div className="text-2xl font-bold">{formatCurrency(kpis?.pipeline_value)}</div>
              <div className="text-xs text-gray-400 mt-1">Non-terminal leads</div>
            </div>
          </div>

          {/* Second row KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Deal Cycle</div>
              <div className="text-2xl font-bold">{kpis?.avg_cycle_days ? `${kpis.avg_cycle_days}d` : '-'}</div>
              <div className="text-xs text-gray-400 mt-1">Lead to close</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Overdue Follow-ups</div>
              <div className="text-2xl font-bold text-orange-600">{kpis?.overdue_followups || 0}</div>
              <div className="text-xs text-orange-500 mt-1">Requires attention</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Hot Leads</div>
              <div className="text-2xl font-bold text-red-600">{kpis?.hot_leads || 0}</div>
              <div className="text-xs text-red-500 mt-1">HOT / VIP bucket</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 invisible">
              {/* spacer */}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Funnel */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-medium mb-4">Sales Funnel</h3>
              <div className="space-y-3">
                {funnel.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No data</div>
                ) : funnel.map((stage, idx) => (
                  <div key={stage.stage_key}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span>{stage.stage_name}</span>
                      <span className="font-medium">{stage.lead_count}</span>
                    </div>
                    <div className="h-6 bg-gray-100 rounded overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${(stage.lead_count / maxFunnelValue) * 100}%`,
                          backgroundColor: stage.stage_color || '#3b82f6'
                        }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{formatCurrency(stage.total_value)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Source Performance */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-medium mb-4">Lead Sources</h3>
              <div className="space-y-2">
                {sourcePerfomance.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No data</div>
                ) : sourcePerfomance.slice(0, 8).map(source => (
                  <div key={source.lead_source} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <div className="font-medium text-sm">{source.lead_source}</div>
                      <div className="text-xs text-gray-500">{source.lead_count} leads</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-sm text-green-600">{source.won_count} won</div>
                      <div className="text-xs text-gray-500">{formatPercent(source.conversion_rate)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Leaderboard */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-medium mb-4">Sales Rep Leaderboard</h3>
              <div className="space-y-2">
                {leaderboard.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No data</div>
                ) : leaderboard.map((rep, idx) => (
                  <div key={rep.user_id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold ${idx === 0 ? 'bg-yellow-100 text-yellow-800' : idx === 1 ? 'bg-gray-100 text-gray-600' : idx === 2 ? 'bg-orange-100 text-orange-800' : 'bg-gray-50 text-gray-500'}`}>
                        {idx + 1}
                      </span>
                      <span className="font-medium text-sm">{rep.rep_name || 'Unknown'}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-sm">{rep.leads_won} won</div>
                      <div className="text-xs text-gray-500">{formatCurrency(rep.revenue_won)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Overdue Follow-ups */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-medium mb-4">Overdue Follow-ups</h3>
              <div className="space-y-2">
                {overdueFollowups.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No overdue follow-ups</div>
                ) : overdueFollowups.map(fu => (
                  <div key={fu.followup_id} className="flex items-center justify-between py-2 border-b last:border-0 bg-red-50 -mx-4 px-4">
                    <div>
                      <div className="font-medium text-sm">{fu.lead_no}</div>
                      <div className="text-xs text-gray-600">{fu.client_name}</div>
                      <div className="text-xs text-red-600">{fu.followup_type} - {new Date(fu.scheduled_date).toLocaleDateString('en-IN')}</div>
                    </div>
                    <div className="text-xs text-gray-500">{fu.assigned_to_name || 'Unassigned'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pipeline Value Summary */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium mb-4">Pipeline Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded">
                <div className="text-sm text-gray-500">Pipeline Value</div>
                <div className="text-xl font-bold text-blue-600">{formatCurrency(kpis?.pipeline_value)}</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded">
                <div className="text-sm text-gray-500">Conversion Rate</div>
                <div className="text-xl font-bold text-green-600">{formatPercent(kpis?.conversion_rate)}</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded">
                <div className="text-sm text-gray-500">Avg Cycle</div>
                <div className="text-xl font-bold text-orange-600">{kpis?.avg_cycle_days ? `${kpis.avg_cycle_days} days` : '-'}</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded">
                <div className="text-sm text-gray-500">Hot Leads</div>
                <div className="text-xl font-bold text-purple-600">{kpis?.hot_leads || 0}</div>
              </div>
            </div>
          </div>

          {/* Lost Reason Analysis */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium mb-4">Lost Reason Analysis</h3>
            {lostAnalysis.length === 0 ? (
              <div className="text-center text-gray-500 py-4">No lost leads in this period</div>
            ) : (() => {
              const totalLost = lostAnalysis.reduce((sum, r) => sum + Number(r.count), 0);
              const maxCount = Math.max(...lostAnalysis.map(r => Number(r.count)), 1);
              return (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-red-50 rounded">
                      <div className="text-sm text-gray-500">Total Lost</div>
                      <div className="text-xl font-bold text-red-600">{totalLost}</div>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded">
                      <div className="text-sm text-gray-500">Total Value Lost</div>
                      <div className="text-xl font-bold text-red-600">
                        {formatCurrency(lostAnalysis.reduce((sum, r) => sum + Number(r.value_lost), 0))}
                      </div>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded">
                      <div className="text-sm text-gray-500">Top Reason</div>
                      <div className="text-xl font-bold text-red-600">{lostAnalysis[0]?.reason_label || lostAnalysis[0]?.lost_reason || '-'}</div>
                    </div>
                  </div>
                  {/* Bar chart + table hybrid */}
                  <div className="space-y-3">
                    {lostAnalysis.map((row) => {
                      const pct = totalLost > 0 ? (Number(row.count) / totalLost * 100) : 0;
                      return (
                        <div key={row.lost_reason}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="font-medium">{row.reason_label || row.lost_reason || 'Unknown'}</span>
                            <span className="text-gray-600">
                              {row.count} lead{Number(row.count) !== 1 ? 's' : ''} ({pct.toFixed(1)}%) &middot; {formatCurrency(row.value_lost)}
                            </span>
                          </div>
                          <div className="h-5 bg-gray-100 rounded overflow-hidden">
                            <div
                              className="h-full bg-red-400 transition-all rounded"
                              style={{ width: `${(Number(row.count) / maxCount) * 100}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default BanquetCrmDashboard;
